import { useState, useEffect, useRef } from 'react';
import { Heart } from 'lucide-react';

interface LikeButtonProps {
  liked?: boolean;
  count?: number;
  onLike: () => void;
  stopPropagation?: boolean;
  style?: React.CSSProperties;
}

export default function LikeButton({ liked, count, onLike, stopPropagation, style }: LikeButtonProps) {
  const [popping, setPopping] = useState(false);
  const prevLiked = useRef(liked);

  useEffect(() => {
    if (liked && !prevLiked.current) {
      setPopping(true);
      const id = setTimeout(() => setPopping(false), 300);
      prevLiked.current = liked;
      return () => clearTimeout(id);
    }
    prevLiked.current = liked;
  }, [liked]);

  function handleClick(e: React.MouseEvent) {
    if (stopPropagation) e.stopPropagation();
    onLike();
  }
  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') {
      if (stopPropagation) e.stopPropagation();
      onLike();
    }
  }
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        cursor: 'pointer',
        flexShrink: 0,
        color: liked ? 'var(--robot-r)' : 'var(--muted)',
        fontSize: 12,
        lineHeight: 1,
        fontFamily: 'var(--font-mono)',
        transition: 'color 0.15s, border-color 0.15s, background 0.15s',
        border: `1px solid ${liked ? 'var(--robot-r)' : 'var(--line)'}`,
        borderRadius: 'var(--radius-pill)',
        padding: '4px 8px',
        background: liked ? 'color-mix(in oklab, var(--robot-r) 10%, var(--surface))' : 'var(--surface)',
        ...style,
      }}
    >
      <span className={popping ? 'anim-heart-pop' : undefined} style={{ display: 'inline-flex' }}>
        <Heart size={13} fill={liked ? 'currentColor' : 'none'} strokeWidth={liked ? 0 : 1.5} />
      </span>
      {count ?? 0}
    </div>
  );
}
